import {
  AnswerValue,
  DualRangeAnswer,
  FormAnswer,
  isDualRangeAnswer,
  isFormAnswer,
  isMultiFieldAnswer,
  MultiFieldAnswer,
  Question,
} from "@/types/agent"

import { QuestionDropdownInput } from "./question-dropdown-input"
import { QuestionDualRangeInput } from "./question-dual-range-input"
import { QuestionFormInput } from "./question-form-input"
import { QuestionMultiFieldInput } from "./question-multi-field-input"
import { QuestionNumberInput } from "./question-number-input"
import { QuestionOtpInput } from "./question-otp-input"
import { QuestionPillsInput } from "./question-pills-input"
import { QuestionRangeInput } from "./question-range-input"
import { QuestionTextInput } from "./question-text-input"

const TOGGLE_OPTIONS = ["Yes", "No"]

export interface QuestionInputProps {
  question: Question
  value: AnswerValue | undefined
  onChange: (value: AnswerValue) => void
  disabled?: boolean
  /** When true, toggle pills do not fall back to config default (custom text active). */
  suppressPillDefault?: boolean
}

export function QuestionInput({
  question,
  value,
  onChange,
  disabled,
  suppressPillDefault = false,
}: QuestionInputProps) {
  const { config, options } = question

  switch (question.input_type) {
    case "single_select":
      return (
        <QuestionPillsInput
          selectionMode="single"
          value={value as string | undefined}
          onChange={onChange}
          disabled={disabled}
          options={options}
          optionGroups={question.option_groups}
          defaultValue={
            typeof config?.default === "string" ? config.default : undefined
          }
        />
      )
    case "range":
      return (
        <QuestionRangeInput
          value={value as number | undefined}
          onChange={onChange}
          disabled={disabled}
          min={config?.min}
          max={config?.max}
          unit={config?.unit}
          defaultValue={
            typeof config?.default === "number" ? config.default : undefined
          }
        />
      )
    case "dual_range":
      return (
        <QuestionDualRangeInput
          value={value as DualRangeAnswer | undefined}
          onChange={onChange}
          disabled={disabled}
          min={config?.min}
          max={config?.max}
          unit={config?.unit}
          defaultMin={config?.defaultMin}
          defaultMax={config?.defaultMax}
        />
      )
    case "dropdown":
      return (
        <QuestionDropdownInput
          value={value as string | undefined}
          onChange={onChange}
          disabled={disabled}
          options={options}
          defaultValue={
            typeof config?.default === "string" ? config.default : undefined
          }
        />
      )
    case "text":
      return (
        <QuestionTextInput
          value={value as string | undefined}
          onChange={onChange}
          disabled={disabled}
          placeholder={config?.placeholder}
        />
      )
    case "multi_select":
    case "pills":
      return (
        <QuestionPillsInput
          selectionMode="multiple"
          value={(value as string[] | undefined) ?? []}
          onChange={onChange}
          disabled={disabled}
          options={options}
        />
      )
    case "number":
      return (
        <QuestionNumberInput
          value={value as number | undefined}
          onChange={onChange}
          disabled={disabled}
          min={config?.min}
          max={config?.max}
          placeholder={config?.placeholder}
        />
      )
    case "toggle": {
      const toggleValue = value as boolean | undefined
      const toggleDefault =
        typeof config?.default === "boolean" ? config.default : undefined
      const pillValue =
        toggleValue === true ? "Yes" : toggleValue === false ? "No" : undefined
      let pillDefault: string | undefined
      if (!suppressPillDefault) {
        if (toggleDefault === true) pillDefault = "Yes"
        else if (toggleDefault === false) pillDefault = "No"
      }
      return (
        <QuestionPillsInput
          selectionMode="single"
          options={TOGGLE_OPTIONS}
          value={pillValue}
          defaultValue={pillDefault}
          onChange={(pill) => onChange(pill === "Yes")}
          disabled={disabled}
        />
      )
    }
    case "multi_field":
      return (
        <QuestionMultiFieldInput
          value={value as MultiFieldAnswer | undefined}
          onChange={onChange}
          disabled={disabled}
          fields={config?.fields ?? []}
        />
      )
    case "form":
      return (
        <QuestionFormInput
          value={value as FormAnswer | undefined}
          onChange={onChange}
          disabled={disabled}
          rows={config?.rows ?? []}
        />
      )
    case "otp":
      return (
        <QuestionOtpInput
          value={value as string | undefined}
          onChange={onChange}
          disabled={disabled}
          length={config?.length}
        />
      )
    default:
      return (
        <QuestionPillsInput
          selectionMode="single"
          value={value as string | undefined}
          onChange={onChange}
          disabled={disabled}
          options={options}
        />
      )
  }
}

// Re-export individual components for direct use if needed
export {
  QuestionDropdownInput,
  QuestionDualRangeInput,
  QuestionFormInput,
  QuestionMultiFieldInput,
  QuestionNumberInput,
  QuestionOtpInput,
  QuestionPillsInput,
  QuestionRangeInput,
  QuestionTextInput,
}

// Helper to check if a question type requires options
export function requiresOptions(question: Question): boolean {
  return ["single_select", "multi_select", "dropdown", "pills"].includes(
    question.input_type
  )
}

// Helper to check if an answer is valid for a question type
export function isAnswerValid(
  question: Question,
  value: AnswerValue | undefined
): boolean {
  if (value === undefined) {
    return question.input_type === "toggle"
  }

  switch (question.input_type) {
    case "toggle":
      return true
    case "multi_select":
    case "pills":
      return Array.isArray(value) && value.length > 0
    case "dual_range":
      return isDualRangeAnswer(value)
    case "multi_field": {
      if (!isMultiFieldAnswer(value)) return false
      const multiFieldValue = value as MultiFieldAnswer
      const fields = question.config?.fields ?? []
      return fields.some((field) => {
        const fieldValue = multiFieldValue[field.id]
        return fieldValue !== undefined && fieldValue !== ""
      })
    }
    case "form": {
      if (!isFormAnswer(value)) return false
      const rows = question.config?.rows ?? []
      // Every non-optional field must be filled in
      return rows.every((row) =>
        row.fields.every((field) => {
          if (field.optional) return true
          const fieldValue = value[field.id]
          return typeof fieldValue === "string" && fieldValue.trim().length > 0
        })
      )
    }
    case "range":
    case "number":
      return typeof value === "number"
    case "single_select":
    case "dropdown":
    case "text":
      return typeof value === "string" && value.length > 0
    case "otp": {
      const expected = question.config?.length
      if (typeof expected !== "number" || expected <= 0) return false
      return (
        typeof value === "string" &&
        value.length === expected &&
        /^\d+$/.test(value)
      )
    }
    default:
      return false
  }
}

// Helper to get the default value for a question
// Used to initialize the answers state with defaults
export function getDefaultValue(question: Question): AnswerValue | undefined {
  const config = question.config

  switch (question.input_type) {
    case "range": {
      return typeof config?.default === "number"
        ? config.default
        : (config?.min ?? 0)
    }
    case "dual_range": {
      const min = config?.min ?? 0
      const max = config?.max ?? 100
      const defaultMin = config?.defaultMin ?? min + (max - min) * 0.25
      const defaultMax = config?.defaultMax ?? min + (max - min) * 0.75
      return { min: defaultMin, max: defaultMax }
    }
    case "number": {
      if (typeof config?.default === "number") return config.default
      return config?.min ?? 1
    }
    case "toggle": {
      return typeof config?.default === "boolean" ? config.default : false
    }
    case "single_select":
    case "dropdown": {
      if (
        typeof config?.default === "string" &&
        question.options.includes(config.default)
      ) {
        return config.default
      }
      return undefined
    }
    case "multi_field": {
      const fields = config?.fields ?? []
      const defaults: MultiFieldAnswer = {}
      let hasDefaults = false

      for (const field of fields) {
        if (field.default !== undefined) {
          defaults[field.id] = field.default
          hasDefaults = true
        }
      }

      return hasDefaults ? defaults : undefined
    }
    case "form": {
      const rows = config?.rows ?? []
      const defaults: FormAnswer = {}
      let hasDefaults = false

      for (const row of rows) {
        for (const field of row.fields) {
          if (field.default !== undefined) {
            defaults[field.id] = field.default
            hasDefaults = true
          }
        }
      }

      return hasDefaults ? defaults : undefined
    }
    default:
      return undefined
  }
}
